import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ChevronLeft, Images, SearchX } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { AlbumAiBar } from "@/components/ai/AlbumAiBar";
import { ChecklistTab } from "@/components/checklist/ChecklistTab";
import { MapTab } from "@/components/map/MapTab";
import { AlbumList } from "@/components/photos/AlbumList";
import { hasActiveFilters, PhotoFilters } from "@/components/photos/PhotoFilters";
import { PhotoGrid } from "@/components/photos/PhotoGrid";
import { PhotoViewer } from "@/components/photos/PhotoViewer";
import { SelectionBar } from "@/components/photos/SelectionBar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { AlbumShareFooter } from "@/components/sharing/AlbumShareFooter";
import { SharingTab } from "@/components/sharing/SharingTab";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAiJobNotifier } from "@/hooks/use-ai-review";
import { usePhotoSelection } from "@/hooks/use-photo-selection";
import {
  type PhotoFilters as Filters,
  photoListFilters,
  usePhotos,
  useProject,
} from "@/hooks/use-projects";
import { useShareSummary } from "@/hooks/use-shares";
import { useCreateZip } from "@/hooks/use-zips";
import { hasPmAccess } from "@/lib/access";
import { ApiError } from "@/lib/api-client";
import { useSession } from "@/lib/auth-client";
import { progressLabel } from "@/lib/checklist";
import type { ProjectDetail } from "@/schemas/photos";

const ROUTE = "/dashboard/projects/$projectId";

function PhotosTab({
  project,
  filters,
  openPhotoId,
  canShare,
  onChange,
  onClear,
  onViewPhoto,
}: {
  project: ProjectDetail;
  /** PM or admin: the viewer shows sharing state and its controls. */
  canShare: boolean;
  filters: Filters;
  /** The photo named in the URL, if any. */
  openPhotoId: string | undefined;
  onChange: (next: Partial<Filters>) => void;
  onClear: () => void;
  /** Open, step or close the viewer. `push` adds a history entry, so back closes it. */
  onViewPhoto: (id: string | undefined, how: "push" | "replace" | "close") => void;
}) {
  const query = usePhotos(project.id, filters);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  // True while the viewer sits on a history entry this page pushed, so Close can go back.
  const pushed = useRef(false);

  const pages = query.data?.pages;
  const photos = useMemo(() => pages?.flatMap((page) => page.data) ?? [], [pages]);
  const total = pages?.[0]?.total ?? 0;
  const selection = usePhotoSelection(total);
  const createZip = useCreateZip();
  const { clear: clearSelection } = selection;
  const photosRef = useRef(photos);
  photosRef.current = photos;
  const { toggle, range } = selection;
  const onSelect = useCallback(
    (photoId: string, mode: "toggle" | "range") =>
      mode === "range" ? range(photoId, photosRef.current) : toggle(photoId),
    [toggle, range],
  );

  // A different list is a different selection: ids picked under one filter mean nothing under another.
  const listKey = JSON.stringify(filters);
  const lastListKey = useRef(listKey);
  useEffect(() => {
    if (lastListKey.current === listKey) return;
    lastListKey.current = listKey;
    clearSelection();
  }, [listKey, clearSelection]);

  // Esc clears the selection, unless it is closing the viewer or leaving a field.
  const viewerOpen = Boolean(openPhotoId);
  useEffect(() => {
    if (viewerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        target.closest("input, select, textarea, [role=listbox], [role=dialog]")
      )
        return;
      clearSelection();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewerOpen, clearSelection]);

  function startDownload() {
    const listFilters = photoListFilters(filters);
    const request = selection.all
      ? {
          projectId: project.id,
          filters: listFilters,
          ...(selection.excluded.length > 0 ? { excludeIds: selection.excluded } : {}),
        }
      : { photoIds: selection.picked };
    const count = selection.count;
    createZip.mutate(request, {
      onSuccess: () => {
        toast.success(`Preparing ${count.toLocaleString("en-US")} photos. Follow it in Downloads.`);
        clearSelection();
      },
    });
  }

  const viewerIndex = openPhotoId ? photos.findIndex((photo) => photo.id === openPhotoId) : -1;

  useEffect(() => {
    if (!openPhotoId) pushed.current = false;
  }, [openPhotoId]);

  // A link can name a photo beyond the first page: keep loading until it turns up, and
  // drop the id if it is not in this list at all (filtered out, withdrawn, mistyped).
  const searching = Boolean(openPhotoId) && viewerIndex === -1 && !query.isPending;
  useEffect(() => {
    if (!searching || isFetchingNextPage || query.isError) return;
    if (hasNextPage) void fetchNextPage();
    else onViewPhoto(undefined, "replace");
  }, [searching, hasNextPage, isFetchingNextPage, query.isError, fetchNextPage, onViewPhoto]);

  const loadMore = useCallback(() => {
    if (!isFetchingNextPage) void fetchNextPage();
  }, [fetchNextPage, isFetchingNextPage]);

  return (
    <div className="space-y-4">
      <PhotoFilters
        project={project}
        filters={filters}
        onChange={onChange}
        onClear={onClear}
        canManage={canShare}
      />
      {canShare && filters.album && <AlbumAiBar project={project} albumId={filters.album} />}
      {selection.count > 0 && (
        <SelectionBar
          selection={selection}
          total={total}
          busy={createZip.isPending}
          onDownload={startDownload}
        />
      )}
      {!query.isPending && !query.isError && (
        <div className="flex flex-wrap items-center gap-3">
          <p data-testid="photo-count" className="text-sm text-muted-foreground tabular-nums">
            {total.toLocaleString("en-US")} {total === 1 ? "photo" : "photos"}
            {hasActiveFilters(filters) &&
              ` of ${project.photoCount.toLocaleString("en-US")} in this project`}
          </p>
          {total > 0 && selection.count === 0 && (
            <Button
              variant="outline"
              size="sm"
              data-testid="select-all"
              onClick={selection.selectAll}
            >
              Select all {total.toLocaleString("en-US")}
            </Button>
          )}
        </div>
      )}
      {query.isError ? (
        <ErrorState message="Could not load the photos" onRetry={() => query.refetch()} />
      ) : !query.isPending && total === 0 ? (
        <EmptyState
          icon={SearchX}
          title="No photos match these filters"
          description="Try a wider date range, or clear the filters."
          className="rounded-lg border border-dashed border-border py-16"
        />
      ) : (
        <PhotoGrid
          photos={photos}
          loading={query.isPending}
          hasNext={hasNextPage}
          fetchingNext={isFetchingNextPage}
          onLoadMore={loadMore}
          isSelected={selection.isSelected}
          selecting={selection.count > 0}
          onSelect={onSelect}
          onOpen={(index) => {
            const photo = photos[index];
            if (!photo) return;
            pushed.current = true;
            onViewPhoto(photo.id, "push");
          }}
        />
      )}
      {viewerIndex >= 0 && (
        <PhotoViewer
          photos={photos}
          total={total}
          albums={project.albums}
          index={viewerIndex}
          hasNext={hasNextPage}
          onIndexChange={(index) => {
            const photo = photos[index];
            if (photo) onViewPhoto(photo.id, "replace");
          }}
          onLoadMore={loadMore}
          sharingProjectId={canShare ? project.id : undefined}
          onClose={() => {
            const goBack = pushed.current;
            pushed.current = false;
            onViewPhoto(undefined, goBack ? "close" : "replace");
          }}
        />
      )}
    </div>
  );
}

function ProjectSummary({ project }: { project: ProjectDetail }) {
  const parts = [
    project.spNumber,
    `${project.photoCount.toLocaleString("en-US")} photos`,
    `${project.albums.length} albums with photos`,
    project.checklist ? progressLabel(project.checklist) : null,
  ].filter(Boolean);
  return parts.join(" · ");
}

export function ProjectPage() {
  const { projectId } = useParams({ from: ROUTE });
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: ROUTE });
  const { data: project, isPending, isError, error, refetch } = useProject(projectId);

  const { data: session } = useSession();
  const canShare = session ? hasPmAccess(session) : false;
  const summary = useShareSummary(projectId, canShare);
  // Tells a PM when an AI check ends, wherever on the page they are.
  useAiJobNotifier(projectId, canShare);

  const { tab: requestedTab, photo, ...filters } = search;
  // The Sharing tab is not there for an IE, a link to it included.
  const tab =
    (requestedTab === "sharing" && !canShare) ||
    (requestedTab === "checklist" && !project?.checklist)
      ? "albums"
      : requestedTab;

  // Any filter or tab change closes the viewer: the photo may not be in the new list.
  function update(next: Partial<typeof search>) {
    void navigate({
      search: (previous) => ({ ...previous, photo: undefined, ...next }),
      replace: true,
    });
  }

  const viewPhoto = useCallback(
    (id: string | undefined, how: "push" | "replace" | "close") => {
      if (how === "close") {
        window.history.back();
        return;
      }
      void navigate({
        search: (previous) => ({ ...previous, photo: id }),
        replace: how === "replace",
        resetScroll: false,
      });
    },
    [navigate],
  );

  function clearFilters() {
    void navigate({ search: { tab: "photos", order: filters.order }, replace: true });
  }

  const back = (
    <Link
      to="/dashboard"
      className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ChevronLeft className="size-4" />
      Projects
    </Link>
  );

  if (isPending) {
    return (
      <>
        {back}
        <Skeleton className="mb-2 h-9 w-72 max-w-full" />
        <Skeleton className="h-5 w-48" />
      </>
    );
  }

  if (isError) {
    return (
      <>
        {back}
        {error instanceof ApiError && error.status === 404 ? (
          <EmptyState
            icon={Images}
            title="Project not found"
            description="It does not exist, or it has not been shared with you."
            className="rounded-lg border border-dashed border-border py-20"
          />
        ) : (
          <ErrorState message="Could not load this project" onRetry={() => refetch()} />
        )}
      </>
    );
  }

  return (
    <>
      {back}
      <PageHeader title={project.name} description={ProjectSummary({ project })} className="mb-4" />
      {project.missingCount > 0 && (
        <p data-testid="missing-note" className="mb-4 text-sm text-muted-foreground">
          {project.missingCount.toLocaleString("en-US")} of these files were not pulled for this
          POC. They are not missing from MESH 1, and their rows are shown here.
        </p>
      )}
      <Tabs
        value={tab}
        onValueChange={(value) =>
          update({
            tab:
              value === "photos" || value === "map" || value === "sharing" || value === "checklist"
                ? value
                : "albums",
          })
        }
      >
        <TabsList>
          <TabsTrigger value="albums">Albums</TabsTrigger>
          <TabsTrigger value="photos">Photos</TabsTrigger>
          <TabsTrigger value="map">Map</TabsTrigger>
          {project.checklist && <TabsTrigger value="checklist">Checklist</TabsTrigger>}
          {canShare && <TabsTrigger value="sharing">Sharing</TabsTrigger>}
        </TabsList>
        <TabsContent value="albums">
          {project.albums.length === 0 ? (
            <EmptyState
              icon={Images}
              title="No photos in this project"
              className="rounded-lg border border-dashed border-border py-20"
            />
          ) : (
            <AlbumList
              albums={project.albums}
              onOpen={(album) =>
                void navigate({ search: { tab: "photos", album: album.id, order: filters.order } })
              }
              footer={
                canShare
                  ? (album) => (
                      <AlbumShareFooter project={project} album={album} summary={summary.data} />
                    )
                  : undefined
              }
            />
          )}
        </TabsContent>
        <TabsContent value="photos">
          <PhotosTab
            project={project}
            filters={filters}
            openPhotoId={photo}
            canShare={canShare}
            onChange={update}
            onClear={clearFilters}
            onViewPhoto={viewPhoto}
          />
        </TabsContent>
        <TabsContent value="map">
          <MapTab
            project={project}
            filters={{ album: filters.album, from: filters.from, to: filters.to }}
            openPhotoId={photo}
            canShare={canShare}
            onChange={update}
            onClear={() =>
              void navigate({ search: { tab: "map", order: filters.order }, replace: true })
            }
            onViewPhoto={viewPhoto}
          />
        </TabsContent>
        {project.checklist && (
          <TabsContent value="checklist">
            <ChecklistTab
              project={project}
              canEdit={canShare}
              onOpenPhoto={(albumId, photoId) =>
                void navigate({
                  search: { tab: "photos", album: albumId, order: filters.order, photo: photoId },
                })
              }
            />
          </TabsContent>
        )}
        {canShare && (
          <TabsContent value="sharing">
            <SharingTab project={project} summary={summary.data} />
          </TabsContent>
        )}
      </Tabs>
    </>
  );
}
